import { DeviceItem, useLocaleStore } from '@/stores'
import {
  hasLightingEditableField,
  LIGHTING_EDITABLE_FIELD,
  LIGHTING_EFFECT_KIND,
  LIGHTING_TARGET,
  LightingDomainType,
  type LightingCapability,
  type LightingConfigData,
  type LightingEffectDefinition,
  type LightingEffectKind,
} from '@rapoo/device-sdk'
import { computed, ref } from 'vue'

type OverviewCard = {
  title: string
  data: { name: string; value: string }[]
}

type LightZoneState = {
  effect: LightingEffectDefinition | null
  config: LightingConfigData | null
}

function resolveDomain(
  target: (typeof LIGHTING_TARGET)[keyof typeof LIGHTING_TARGET],
  kind?: LightingEffectKind,
): LightingDomainType {
  if (target === LIGHTING_TARGET.Side) return LightingDomainType.Side
  if (target === LIGHTING_TARGET.Indicator) return LightingDomainType.Indicator
  if (kind === LIGHTING_EFFECT_KIND.Static) return LightingDomainType.Static
  return LightingDomainType.Dynamic
}

function emptyZone(): LightZoneState {
  return { effect: null, config: null }
}

/**
 * 概览页键盘灯光：一次查询按键灯 / 侧灯 / 装饰灯 / 充电灯，供四张卡片展示。
 * 亮度、速度仅在当前生效灯效的 editableFields 支持时输出。
 */
export function useKeyboardLighting(itemRef: { value: DeviceItem }) {
  const { t } = useLocaleStore()
  const device = computed(() => itemRef.value.original)

  const keyLight = ref<LightZoneState>(emptyZone())
  const sideLight = ref<LightZoneState>(emptyZone())
  const decorLight = ref<LightZoneState>(emptyZone())
  const chargeLight = ref<LightZoneState>(emptyZone())

  const lightingCapability = computed(() =>
    device.value?.manifest.capabilities.find((c): c is LightingCapability => c.type === 'lighting'),
  )

  const hasSideLighting = computed(() =>
    Boolean(
      lightingCapability.value?.config.effects.some(
        (effect) => effect.target === LIGHTING_TARGET.Side,
      ),
    ),
  )

  const hasIndicatorLighting = computed(() =>
    Boolean(
      lightingCapability.value?.config.effects.some(
        (effect) => effect.target === LIGHTING_TARGET.Indicator,
      ),
    ),
  )

  const buildEffectCard = (
    title: string,
    zone: LightZoneState,
    options?: { includeEffectName?: boolean },
  ): OverviewCard | null => {
    const { effect, config } = zone
    if (!effect) return null

    const includeEffectName = options?.includeEffectName ?? true
    const data: { name: string; value: string }[] = []

    if (includeEffectName && effect.i18nKey) {
      data.push({
        name: t('home.overview.keyboardLighting.effectSetting'),
        value: t(effect.i18nKey),
      })
    }

    if (
      hasLightingEditableField(effect, LIGHTING_EDITABLE_FIELD.Brightness) &&
      config?.brightness !== undefined
    ) {
      data.push({
        name: t('home.overview.keyboardLighting.brightness'),
        value: String(config.brightness),
      })
    }

    if (
      hasLightingEditableField(effect, LIGHTING_EDITABLE_FIELD.Speed) &&
      config?.speed !== undefined
    ) {
      data.push({
        name: t('home.overview.keyboardLighting.speed'),
        value: String(config.speed),
      })
    }

    if (!data.length) return null
    return { title, data }
  }

  const readZone = async (
    effects: LightingEffectDefinition[],
    modeId: number,
    target: (typeof LIGHTING_TARGET)[keyof typeof LIGHTING_TARGET],
  ): Promise<LightZoneState> => {
    const lighting = device.value.features.keyboardLighting
    const effect = effects.find((e) => e.id === modeId) ?? null
    if (!lighting || !effect) return emptyZone()

    try {
      const config = await lighting.getConfig(resolveDomain(target, effect.kind), modeId)
      return { effect, config }
    } catch (err) {
      console.warn(`[useKeyboardLighting] failed to read ${target} mode=${modeId}:`, err)
      return { effect, config: null }
    }
  }

  const initKeyboardLightingData = async () => {
    keyLight.value = emptyZone()
    sideLight.value = emptyZone()
    decorLight.value = emptyZone()
    chargeLight.value = emptyZone()

    if (device.value?.manifest.type !== 'keyboard') return

    const lighting = device.value.features.keyboardLighting
    if (!lighting || !lightingCapability.value?.config.enable) return

    const tasks: Promise<void>[] = []

    tasks.push(
      (async () => {
        try {
          const [effects, modeId] = await Promise.all([
            lighting.getEffects(LIGHTING_TARGET.Keyboard),
            lighting.getCurrentEffect(),
          ])
          keyLight.value = await readZone(effects, modeId, LIGHTING_TARGET.Keyboard)
        } catch (err) {
          console.warn('[useKeyboardLighting] failed to read key light:', err)
        }
      })(),
    )

    if (hasSideLighting.value && lighting.getSideEffect) {
      tasks.push(
        (async () => {
          try {
            const [effects, modeId] = await Promise.all([
              lighting.getEffects(LIGHTING_TARGET.Side),
              lighting.getSideEffect!(),
            ])
            sideLight.value = await readZone(effects, modeId, LIGHTING_TARGET.Side)
          } catch (err) {
            console.warn('[useKeyboardLighting] failed to read side light:', err)
          }
        })(),
      )
    }

    if (hasIndicatorLighting.value) {
      tasks.push(
        (async () => {
          try {
            const effects = await lighting.getEffects(LIGHTING_TARGET.Indicator)
            const ambient = effects.find((e) => e.kind === LIGHTING_EFFECT_KIND.Ambient)
            const charge = effects.find((e) => e.kind === LIGHTING_EFFECT_KIND.Indicator)

            await Promise.all([
              (async () => {
                if (!ambient) return
                decorLight.value = await readZone(effects, ambient.id, LIGHTING_TARGET.Indicator)
              })(),
              (async () => {
                if (!charge) return
                chargeLight.value = await readZone(effects, charge.id, LIGHTING_TARGET.Indicator)
              })(),
            ])
          } catch (err) {
            console.warn('[useKeyboardLighting] failed to read indicator lights:', err)
          }
        })(),
      )
    }

    await Promise.all(tasks)
  }

  const keyLightData = computed(() =>
    buildEffectCard(t('home.overview.keyboardLighting.keyLight'), keyLight.value),
  )

  const sideLightData = computed(() =>
    buildEffectCard(
      t(
        lightingCapability.value?.config.tabI18nKeys?.[LIGHTING_TARGET.Side] ??
          'home.overview.keyboardLighting.sideLight',
      ),
      sideLight.value,
    ),
  )

  // 装饰灯 / 充电灯本身即灯区类型，概览只展示亮度（及灯效支持的速度）
  const decorLightData = computed(() =>
    buildEffectCard(t('home.overview.keyboardLighting.decorLight'), decorLight.value, {
      includeEffectName: false,
    }),
  )

  const chargeLightData = computed(() =>
    buildEffectCard(t('home.overview.keyboardLighting.chargeLight'), chargeLight.value, {
      includeEffectName: false,
    }),
  )

  const keyboardLightingCards = computed(() =>
    [keyLightData.value, sideLightData.value, decorLightData.value, chargeLightData.value].filter(
      (card): card is OverviewCard => !!card,
    ),
  )

  return {
    keyLightData,
    sideLightData,
    decorLightData,
    chargeLightData,
    keyboardLightingCards,
    initKeyboardLightingData,
  }
}
